import { getDbOrThrow } from "../../core/firestoreCore";
import { COLLECTIONS, userScopedId, slugify } from "../../core/collections";
import {
  collection,
  doc,
  serverTimestamp,
  setDoc,
  updateDoc,
  getDoc,
} from "firebase/firestore";
import { isDryRunFlag, logDryRunBanner, coerceUser } from "../../core/writerUtils";

/* ------------------------------------------------------------------ */
/* Single-record helpers: Accounts                                    */
/* ------------------------------------------------------------------ */

export async function createUserAccount(payload, { dryRun } = {}) {
  const db = getDbOrThrow();

  const {
    // 🚫 exclude user fields from what we persist
    user: _ignoredUser,
    userId: _ignoredUserId,
    userEmail: _ignoredUserEmail,

    name,
    currencyCode = null,
    systemTypeId,
    systemTypeName,
    systemTypeDesc,
    balance,
    alertThreshold,
    ...rest
  } = payload || {};

  const { uid, email } = coerceUser(payload);
  if (!name) throw new Error("createUserAccount: name is required");
  if (!systemTypeId) throw new Error("createUserAccount: systemTypeId is required");

  const s = slugify(name);
  const id = userScopedId(uid, s);
  const ref = doc(collection(db, COLLECTIONS.userAccounts), id);

  const DRY = isDryRunFlag(dryRun);
  const now = serverTimestamp();

  // Only persist plain data; user/userId/userEmail are handled separately.
  const record = {
    ...rest,
    userId: uid,
    userEmail: email,
    name,
    slug: s,
    currencyCode,
    systemTypeId,
    systemTypeName: systemTypeName ?? systemTypeId,
    systemTypeDesc: systemTypeDesc ?? "",
    ...(balance !== undefined ? { accountBalance: balance } : {}),
    ...(alertThreshold !== undefined ? { alertThreshold } : {}),
    source: "ui-manual",
    createdAt: now,
    updatedAt: now,
  };

  if (DRY) {
    logDryRunBanner("createUserAccount", { id, name, currencyCode, systemTypeId });
  } else {
    const existing = await getDoc(ref);
    if (existing.exists()) {
      throw new Error(`Account "${name}" already exists.`);
    }
    await setDoc(ref, record, { merge: true });
  }

  return { id, ...record, __dryRun: DRY };
}

export async function updateUserAccount({ id, updates }, { dryRun } = {}) {
  const db = getDbOrThrow();
  if (!id) throw new Error("updateUserAccount: missing id");

  const DRY = isDryRunFlag(dryRun);

  // Never allow these through in updates
  const FORBIDDEN_KEYS = new Set(["user", "userId", "userEmail", "createdAt"]);

  const base = {
    ...updates,
    ...(updates && Object.prototype.hasOwnProperty.call(updates, "balance")
      ? { accountBalance: updates.balance }
      : {}),
    updatedAt: serverTimestamp(),
  };

  const patch = Object.fromEntries(
    Object.entries(base).filter(([k, v]) => !FORBIDDEN_KEYS.has(k) && v !== undefined)
  );

  const ref = doc(collection(db, COLLECTIONS.userAccounts), id);

  if (DRY) {
    logDryRunBanner("updateUserAccount", { id, keys: Object.keys(patch) });
  } else {
    await updateDoc(ref, patch);
  }

  return true;
}
